import React, { useCallback, useEffect, useState } from 'react'
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CLoadingButton,
  CPlaceholder,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilChartPie, cilClock, cilCloudDownload, cilFlagAlt, cilPeople } from '@coreui/icons'

import RivalTrackerService from 'src/service/RivalTrackerService'
import CommonService from 'src/service/CommonService'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import ToastComponent from 'src/components/common/TaostComponent'
import { InfoBox } from 'src/components/common/ImportWizard'
import LoadError from 'src/views/Vote/LoadError'
import MemberRivals from './MemberRivals'
import { formatDate, formatTime, initialsOf } from './helpers'
import './RivalTracker.css'

const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Member' },
  { key: 'email', label: 'Email' },
]

const columns = [
  { label: 'Member', key: 'full_name', filter: false, sorter: true },
  { label: 'Their Team', key: 'team_name', filter: false, sorter: true },
  { label: 'Rivals', key: 'rival_count', filter: false, sorter: true, _style: { width: '1%' } },
  { label: 'Last Added', key: 'last_added_at', filter: false, sorter: true },
  { label: '', key: 'actions', filter: false, sorter: false, _style: { width: '1%' } },
]

const DEFAULT_SORT = { column: 'rival_count', state: 'desc' }

const csvCell = (value) => {
  if (value === null || value === undefined) return ''
  const text = String(value)
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const CSV_COLUMNS = [
  ['Member', (row) => row.full_name],
  ['Email', (row) => row.email],
  ['Their Team', (row) => row.team_name],
  ['Rivals Tracked', (row) => row.rival_count],
  ['First Added', (row) => row.first_added_at],
  ['Last Added', (row) => row.last_added_at],
]

const RivalTracker = () => {
  const [summary, setSummary] = useState(null)
  const [summaryError, setSummaryError] = useState(false)

  const [members, setMembers] = useState()
  const [loading, setLoading] = useState(false)
  const [tableError, setTableError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState(DEFAULT_SORT)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('full_name')

  const [selectedMember, setSelectedMember] = useState(null)
  const [exporting, setExporting] = useState(false)

  const loadSummary = useCallback(() => {
    setSummaryError(false)
    RivalTrackerService.getSummary()
      .then((res) => {
        if (res.success) setSummary(res.data)
        else setSummaryError(true)
      })
      .catch(() => setSummaryError(true))
  }, [])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const fetchMembers = useCallback(() => {
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    if (columnSorter?.column && columnSorter.state) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    setTableError(false)
    RivalTrackerService.getMembers(itemsPerPage, activePage, params)
      .then((res) => {
        if (res.success) setMembers(res.data)
        else setTableError(true)
      })
      .catch(() => setTableError(true))
      .finally(() => setLoading(false))
  }, [activePage, columnFilter, columnSorter, itemsPerPage])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const exportCsv = async () => {
    setExporting(true)
    try {
      const [res, game] = await Promise.all([
        RivalTrackerService.getAllMembers(),
        CommonService.getGameName().catch(() => null),
      ])
      const rows = res?.data || []
      if (!rows.length) {
        ToastComponent('There are no rival trackers to download yet', 'error')
        return
      }
      const csv = [
        CSV_COLUMNS.map(([label]) => csvCell(label)).join(','),
        ...rows.map((row) => CSV_COLUMNS.map(([, cell]) => csvCell(cell(row))).join(',')),
      ].join('\n')
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `MyClubtap-${game?.game_name || 'Game'}-Rival-Tracker.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      ToastComponent('Rival Tracker log downloaded', 'success')
    } catch (e) {
      ToastComponent('Could not download the Rival Tracker log', 'error')
    } finally {
      setExporting(false)
    }
  }

  const retryAll = () => {
    loadSummary()
    fetchMembers()
  }

  const hasSearch = Boolean(columnFilter[searchField])
  const nothingYet = summary && summary.total_rivals === 0

  const STAT_CARDS = [
    {
      label: 'Rival Trackers',
      value: summary?.total_rivals,
      note: 'Across the whole game',
      icon: cilFlagAlt,
      color: 'primary',
    },
    {
      label: 'Members Tracking',
      value: summary?.members_tracking,
      note: summary ? `${summary.adoption_pct}% of ${summary.members_in_game} members` : '',
      progress: summary?.adoption_pct,
      icon: cilPeople,
      color: 'info',
    },
    {
      label: 'Average per Member',
      value: summary?.avg_per_member,
      note: 'Among members who track rivals',
      icon: cilChartPie,
      color: 'success',
    },
    {
      label: 'Added in Last 7 Days',
      value: summary?.added_last_7_days,
      note: summary?.last_added_at
        ? `Last added ${formatDate(summary.last_added_at)}`
        : 'None added yet',
      icon: cilClock,
      color: 'warning',
    },
  ]

  const mostTracked = summary?.most_tracked || []
  const topTrackers = mostTracked.reduce((max, row) => Math.max(max, Number(row.trackers)), 0)

  return (
    <>
      <div className="ge-page-header d-flex flex-wrap align-items-start justify-content-between gap-3">
        <div>
          <h1 className="ge-page-title">Rival Tracker</h1>
          <p className="ge-page-subtitle">
            How members use Rival Tracker in the app - who is tracking rivals, how many, and which
            teams they are chasing.
          </p>
        </div>
        <CLoadingButton
          color="secondary"
          variant="outline"
          loading={exporting}
          disabled={exporting || nothingYet}
          onClick={exportCsv}
        >
          <CIcon icon={cilCloudDownload} className="me-2" />
          Export CSV
        </CLoadingButton>
      </div>

      {summaryError && (
        <LoadError
          className="mb-3"
          message="We couldn't load the Rival Tracker numbers."
          onRetry={retryAll}
        />
      )}

      <CRow className="g-3 mb-3">
        {STAT_CARDS.map((s) => (
          <CCol sm={6} xl={3} key={s.label}>
            <CCard className="h-100 rt-stat-card">
              <CCardBody className="d-flex justify-content-between align-items-start gap-2">
                <div className="min-w-0 flex-grow-1">
                  <div className="text-medium-emphasis small">{s.label}</div>
                  {summary ? (
                    <div className="fs-4 fw-semibold lh-sm">{s.value ?? '-'}</div>
                  ) : (
                    <CPlaceholder animation="glow" className="d-block my-1">
                      <CPlaceholder xs={5} size="lg" />
                    </CPlaceholder>
                  )}
                  {s.progress !== undefined && summary && (
                    <div className="rt-progress mt-2" aria-hidden="true">
                      <span style={{ width: `${Math.min(100, s.progress)}%` }} />
                    </div>
                  )}
                  {s.note && <div className="text-medium-emphasis small mt-1">{s.note}</div>}
                </div>
                <div className={`ge-stat-icon ge-stat-icon--${s.color}`}>
                  <CIcon icon={s.icon} size="lg" />
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>

      <InfoBox className="small">
        Counts are the rivals each member is tracking right now - when a member removes a rival in
        the app it stops counting.
        {summary?.timezone && (
          <> Dates are shown in the game&apos;s timezone ({summary.timezone}).</>
        )}
      </InfoBox>

      {nothingYet && !hasSearch ? (
        <CCard className="rt-panel">
          <CCardBody className="rt-empty">
            <div className="ge-stat-icon ge-stat-icon--primary rt-empty-icon">
              <CIcon icon={cilFlagAlt} size="xl" />
            </div>
            <div className="rt-empty-title">No rival trackers yet</div>
            <p className="rt-empty-text">
              Members add rivals from <strong>Rival Tracker</strong> in the app to compare their
              fantasy team against another manager. As soon as someone adds one, it will show up
              here.
            </p>
          </CCardBody>
        </CCard>
      ) : (
        <CRow className="g-3">
          <CCol xs={12} xl={8}>
            <CCard className="rt-panel h-100">
              <CCardBody>
                <div className="rt-panel-heading">
                  <h2 className="rt-panel-title">Members Tracking Rivals</h2>
                  <p className="rt-panel-note">
                    One row per member. Select a member to see exactly who they are tracking.
                  </p>
                </div>

                <TableSearchToolbar
                  fields={SEARCH_FIELDS}
                  field={searchField}
                  onFieldChange={handleFieldChange}
                  value={columnFilter?.[searchField] || ''}
                  onSearch={applySearch}
                  placeholder={`Search by ${
                    SEARCH_FIELDS.find((f) => f.key === searchField)?.label
                  }...`}
                />

                {tableError ? (
                  <LoadError message="We couldn't load the member list." onRetry={fetchMembers} />
                ) : (
                  <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
                    <CSmartTable
                      columns={columns}
                      columnSorter={{ external: true }}
                      sorterValue={columnSorter}
                      noItemsLabel={
                        hasSearch
                          ? 'No members match your search'
                          : 'No members are tracking rivals'
                      }
                      clickableRows
                      onRowClick={(item) => setSelectedMember(item)}
                      scopedColumns={{
                        full_name: (item) => (
                          <td>
                            <div className="d-flex align-items-center gap-2 min-w-0">
                              <span className="rt-avatar" aria-hidden="true">
                                {initialsOf(item.full_name)}
                              </span>
                              <div className="min-w-0">
                                <div className="fw-semibold text-truncate">
                                  {item.full_name || 'Unknown member'}
                                </div>
                                {item.email && (
                                  <div className="rt-muted small text-truncate">{item.email}</div>
                                )}
                              </div>
                            </div>
                          </td>
                        ),
                        team_name: (item) => (
                          <td className={item.team_name ? undefined : 'rt-muted'}>
                            {item.team_name || '-'}
                          </td>
                        ),
                        rival_count: (item) => (
                          <td className="text-center">
                            <CBadge color="primary" shape="rounded-pill" className="rt-count">
                              {item.rival_count}
                            </CBadge>
                          </td>
                        ),
                        last_added_at: (item) => (
                          <td className="text-nowrap">
                            <div>{formatDate(item.last_added_at)}</div>
                            <div className="rt-muted small">{formatTime(item.last_added_at)}</div>
                          </td>
                        ),
                        actions: (item) => (
                          <td className="text-end">
                            <CButton
                              color="primary"
                              variant="outline"
                              size="sm"
                              className="text-nowrap"
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedMember(item)
                              }}
                            >
                              View rivals
                            </CButton>
                          </td>
                        ),
                      }}
                      items={members?.data}
                      itemsPerPage={itemsPerPage}
                      itemsPerPageSelect
                      loading={loading}
                      pagination={{ external: true }}
                      paginationProps={{
                        activePage,
                        pages: Math.ceil(members?.total / itemsPerPage) || 1,
                      }}
                      tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                      onActivePageChange={(page) => setActivePage(page)}
                      onItemsPerPageChange={(perPage) => {
                        setActivePage(1)
                        setItemsPerPage(perPage)
                      }}
                      onSorterChange={(sorter) =>
                        setColumnSorter(sorter?.state ? sorter : DEFAULT_SORT)
                      }
                    />
                  </div>
                )}
              </CCardBody>
            </CCard>
          </CCol>

          <CCol xs={12} xl={4}>
            <CCard className="rt-panel h-100">
              <CCardBody>
                <div className="rt-panel-heading">
                  <h2 className="rt-panel-title">Most Tracked Teams</h2>
                  <p className="rt-panel-note">
                    The teams the most members have picked as a rival.
                  </p>
                </div>

                {!summary ? (
                  <CPlaceholder component="div" animation="glow">
                    {[0, 1, 2, 3].map((i) => (
                      <CPlaceholder key={i} xs={12} size="lg" className="mb-3 rounded" />
                    ))}
                  </CPlaceholder>
                ) : mostTracked.length === 0 ? (
                  <div className="rt-muted small py-3">No teams are being tracked yet.</div>
                ) : (
                  <ol className="rt-ranked">
                    {mostTracked.map((row, index) => (
                      <li key={row.team_id} className="rt-ranked-row">
                        <span
                          className="rt-ranked-fill"
                          style={{
                            width: `${Math.max(6, (Number(row.trackers) / topTrackers) * 100)}%`,
                          }}
                          aria-hidden="true"
                        />
                        <span
                          className={`rt-ranked-rank${index === 0 ? ' rt-ranked-rank--top' : ''}`}
                        >
                          {index + 1}
                        </span>
                        <span className="rt-ranked-label">
                          <span className="fw-semibold text-truncate d-block">
                            {row.team_name || 'Unnamed team'}
                          </span>
                          {row.owner_name && (
                            <span className="rt-muted small text-truncate d-block">
                              {row.owner_name}
                            </span>
                          )}
                        </span>
                        <span className="rt-ranked-value">
                          {row.trackers}
                          <span className="rt-muted small">
                            {' '}
                            {Number(row.trackers) === 1 ? 'tracker' : 'trackers'}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      <MemberRivals member={selectedMember} onClose={() => setSelectedMember(null)} />
    </>
  )
}

export default RivalTracker
